import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, SectionTitle, PageLoader, StatusBadge, Btn, TextInput } from "@/lib/ui";
import { BRAND_ACCENTS } from "@/lib/ui";
import { TrendingUp, Plug, LineChart, Activity, ArrowUpRight, CheckCircle2, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const hsl = (v) => `hsl(${v || "46 68% 52%"})`;

const PLATFORM_META = {
  ga4: { label: "Google Analytics 4", icon: LineChart, fields: [
    { key: "measurement_id", label: "Measurement ID", placeholder: "G-XXXXXXXXXX", hint: "Client-side tag (Admin → Data Streams)." },
    { key: "property_id", label: "Property ID", placeholder: "123456789", hint: "Numeric — needed later for server-side reports." },
  ] },
  meta_pixel: { label: "Meta Pixel", icon: Activity, fields: [
    { key: "pixel_id", label: "Pixel ID", placeholder: "1234567890", hint: "Events Manager → Data Sources." },
    { key: "dataset_id", label: "Dataset ID (optional)", placeholder: "", hint: "For the Conversions API." },
    { key: "access_token", label: "CAPI Access Token (optional)", placeholder: "", hint: "Stored securely; never shown again." },
  ] },
};

function ConnectDialog({ open, onClose, brand, platform }) {
  const api = useApi();
  const qc = useQueryClient();
  const meta = PLATFORM_META[platform] || {};
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open && brand && platform) {
      const cfg = brand.platforms?.[platform]?.config || {};
      const init = {};
      (meta.fields || []).forEach((f) => { init[f.key] = cfg[f.key] || ""; });
      setForm(init);
    }
  }, [open, brand?.code, platform]); // eslint-disable-line

  const save = useMutation({
    mutationFn: () => api.put(`/growth/connections/${brand.code}/${platform}`, form),
    onSuccess: () => {
      toast.success(`${meta.label} saved for ${brand.name}`);
      qc.invalidateQueries({ queryKey: ["growth-connections"] });
      qc.invalidateQueries({ queryKey: ["growth-overview"] });
      qc.invalidateQueries({ queryKey: ["exec-dashboard"] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  const disconnect = useMutation({
    mutationFn: () => api.del(`/growth/connections/${brand.code}/${platform}`),
    onSuccess: () => {
      toast.success("Disconnected");
      qc.invalidateQueries({ queryKey: ["growth-connections"] });
      qc.invalidateQueries({ queryKey: ["growth-overview"] });
      qc.invalidateQueries({ queryKey: ["exec-dashboard"] });
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });

  if (!brand || !platform) return null;
  const connected = brand.platforms?.[platform]?.status === "connected";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{meta.label}</DialogTitle>
          <DialogDescription>Connect {meta.label} for <span className="text-foreground">{brand.name}</span>. Enter the IDs now — data flows once the account is live.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(meta.fields || []).map((f) => (
            <div key={f.key}>
              <TextInput label={f.label} placeholder={f.placeholder}
                value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                data-testid={`growth-field-${platform}-${f.key}`} />
              {f.hint && <p className="text-[11px] text-muted-foreground mt-1">{f.hint}</p>}
            </div>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <Btn onClick={() => save.mutate()} disabled={save.isPending} data-testid="growth-save" className="flex-1">{save.isPending ? "Saving…" : connected ? "Update" : "Connect"}</Btn>
            {connected && <Btn variant="outline" onClick={() => disconnect.mutate()} disabled={disconnect.isPending} data-testid="growth-disconnect">Disconnect</Btn>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PlatformPill({ cell, onClick, testid }) {
  const meta = PLATFORM_META[cell.platform];
  const on = cell.status === "connected";
  return (
    <button onClick={onClick} data-testid={testid}
      className={cn("flex items-center gap-2 rounded-lg neu-inset px-3 py-2 text-left w-full transition-colors",
        on ? "" : "opacity-90")}>
      <meta.icon className="h-4 w-4 shrink-0" style={{ color: on ? "hsl(145 60% 62%)" : "hsl(0 0% 50%)" }} />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate">{meta.label}</div>
        <div className="text-[10px] text-muted-foreground truncate">
          {on ? (cell.config?.measurement_id || cell.config?.pixel_id || "Connected") : "Not connected"}
        </div>
      </div>
      {on ? <CheckCircle2 className="h-4 w-4 text-ok shrink-0" /> : <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
    </button>
  );
}

export default function Growth() {
  const api = useApi();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState({ open: false, brand: null, platform: null });
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["growth-connections"], queryFn: () => api.get("/growth/connections") });
  const { data: overview } = useQuery({ queryKey: ["growth-overview"], queryFn: () => api.get("/growth/overview") });

  if (isLoading) return <PageLoader />;
  const s = overview?.summary || { connected: 0, total: 0 };

  return (
    <div data-testid="growth-page">
      <PageHeader kicker="Growth & Marketing" title="Analytics & Pixels">
        <TrendingUp className="h-5 w-5 text-primary" />
        <StatusBadge tone={s.connected === s.total && s.total > 0 ? "ok" : "gold"}>{s.connected}/{s.total} connected</StatusBadge>
      </PageHeader>

      <div className="neu-inset rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="growth-intro">
        <Plug className="h-4 w-4 text-primary mt-0.5 shrink-0" />
        <p className="text-sm text-muted-foreground">Each portal has its own GA4 property and Meta Pixel. Add the IDs here to make setup plug-and-play — the moment you create an account, paste its ID and it's wired. Live metric pulls are enabled in a later phase.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((b) => (
          <div key={b.code} className="neu-card rounded-2xl p-5" data-testid={`growth-portal-${b.code}`} style={{ borderTop: `2px solid ${hsl(BRAND_ACCENTS[b.code] || b.accent)}` }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: hsl(BRAND_ACCENTS[b.code] || b.accent) }} />
                <span className="text-sm font-medium truncate">{b.name}</span>
              </div>
              <button onClick={() => navigate(`/growth/${b.code}`)} className="text-xs text-primary hover:underline flex items-center gap-1 shrink-0" data-testid={`growth-drill-${b.code}`}>
                Details <ArrowUpRight className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-2">
              <PlatformPill cell={b.platforms.ga4} testid={`growth-connect-${b.code}-ga4`} onClick={() => setDialog({ open: true, brand: b, platform: "ga4" })} />
              <PlatformPill cell={b.platforms.meta_pixel} testid={`growth-connect-${b.code}-meta_pixel`} onClick={() => setDialog({ open: true, brand: b, platform: "meta_pixel" })} />
            </div>
          </div>
        ))}
      </div>

      <ConnectDialog open={dialog.open} brand={dialog.brand} platform={dialog.platform}
        onClose={() => setDialog({ open: false, brand: null, platform: null })} />
    </div>
  );
}
